import React, { useState } from "react";
import { Box, Button, Menu, Tooltip, IconButton } from "@mui/material";
import TagFilter from "./TagFilter";
import KeyOperatorFilter from "./KeyOperatorFilter";
import ResetButton from "components/ResetButton";
import { PaginationDropdown } from "components/Pagination";
import SearchInput from "components/SearchInput";
import FreeToExecuteFilter from "./FreeToExecuteFilter";
import VideoFilter from "./VideoFilter";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";

const FilterSection = ({
  problems,
  isAdmin,
  isAdminContext,
  tags,
  selectedTags,
  keysWithOperators,
  setKeysWithOperators,
  setCurrentPage,
  search,
  rows,
  onTagChange,
  onKeyChange,
  onSearchChange,
  onReset,
  onRowsPerPageChange,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "1fr auto",
          md: "3fr 2fr",
        },
        gap: { xs: 1, sm: 2 },
        alignItems: "center",
        role: "toolbar",
        px: { xs: 1, sm: 2 },
        py: { xs: 1, sm: 1.5 },
        "& > *": {
          minWidth: 0,
        },
      }}
    >
      {/* Search Bar */}
      <Box
        sx={{
          gridColumn: { xs: "1 / -1", sm: "1 / 2" },
          minWidth: 0,
        }}
      >
        <SearchInput
          value={search}
          onChange={(value) => {
            onSearchChange(value);
            setCurrentPage(1);
          }}
          label={isAdminContext ? "Search by filters" : "Search problems"}
          type="search"
          sx={{
            width: "100%",
            maxWidth: { sm: "400px", md: "600px" },
            minWidth: "120px",
            bgcolor: "background.paper",
            "& .MuiInputBase-root": {
              transition: "all 0.2s ease",
              padding: "4px 8px",
            },
            "&:hover .MuiInputBase-root": {
              boxShadow: (theme) => theme.shadows[2],
            },
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
            }
          }}
          aria-label="Search problems"
        />
      </Box>
      {/* Filters and Controls */}
      <Box
        sx={{
          gridColumn: { xs: "1 / -1", sm: "2 / 3" },
          justifySelf: { xs: "end" },
          display: "flex",
          flexWrap: { xs: "wrap", sm: "nowrap" },
          alignItems: "center",
          minWidth: 0,
          "& > *": {
            flexShrink: 0,
          },
        }}
      >
        {/* Filter Menu Button */}
        <Tooltip title="Apply filters">
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={handleFilterClick}
            sx={{
              px: { xs: 1.5, sm: 2 },
              py: 0.5,
              fontSize: "0.875rem",
              borderRadius: 1,
              textTransform: "none",
              minWidth: "100px",
              bgcolor:
                selectedTags.length > 0 || keysWithOperators.length > 0
                  ? "action.selected"
                  : "transparent",
              "&:hover": {
                bgcolor: "action.hover",
              },
            }}
            aria-label="Open filter menu"
          >
            Filters
          </Button>
        </Tooltip>
        {/* Filter Menu */}
        <Menu
          id="filter-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleFilterClose}
          PaperProps={{
            sx: {
              minWidth: { xs: "200px", sm: "280px" },
              maxWidth: "400px",
              p: 1,
              borderRadius: 2,
              boxShadow: (theme) => theme.shadows[4],
            },
          }}
          MenuListProps={{
            "aria-labelledby": "filter-button",
            sx: { display: "flex", flexDirection: "column", gap: 1 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box
              component="h3"
              sx={{ fontSize: "1rem", fontWeight: "medium", m: 0 }}
            >
              Filters
            </Box>
            <IconButton
              onClick={handleFilterClose}
              size="small"
              aria-label="Close filter menu"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          {!isAdminContext && (
            <Box sx={{ minWidth: "100%" }}>
              <TagFilter
                tags={tags}
                selectedTags={selectedTags}
                onTagChange={(newTags) => {
                  onTagChange(newTags);
                  setCurrentPage(1);
                }}
                sx={{ width: "100%" }}
              />
            </Box>
          )}
          {isAdminContext && (
            <Box sx={{ minWidth: "100%" }}>
              <KeyOperatorFilter
                keysWithOperators={keysWithOperators}
                onKeyChange={(newKeys) => {
                  onKeyChange(newKeys);
                  setCurrentPage(1);
                }}
                sx={{ width: "100%" }}
              />
            </Box>
          )}
          <Box sx={{ minWidth: "100%" }}>
            <FreeToExecuteFilter
              keysWithOperators={keysWithOperators}
              setKeysWithOperators={(newKeys) => {
                setKeysWithOperators(newKeys);
                setCurrentPage(1);
              }}
              setCurrentPage={setCurrentPage}
              sx={{ width: "100%" }}
            />
          </Box>
          <Box sx={{ minWidth: "100%" }}>
            <VideoFilter
              keysWithOperators={keysWithOperators}
              setKeysWithOperators={(newKeys) => {
                setKeysWithOperators(newKeys);
                setCurrentPage(1);
              }}
              setCurrentPage={setCurrentPage}
              problems={problems}
              sx={{ width: "100%" }}
            />
          </Box>
        </Menu>
        {/* Reset and Pagination */}
        {(selectedTags.length > 0 || keysWithOperators.length > 0) && (
          <Tooltip title="Clear all filters">
            <ResetButton
              onReset={() => {
                onReset();
                setCurrentPage(1);
              }}
              sx={{
                px: { xs: 1.5, sm: 2 },
                py: 0.5,
                fontSize: "0.875rem",
                minWidth: "80px",
                borderRadius: 1,
              }}
            />
          </Tooltip>
        )}
        <Tooltip title="Items per page">
          <PaginationDropdown
            rows={rows}
            onRowsPerPageChange={onRowsPerPageChange}
            sx={{
              minWidth: "80px",
              "& .MuiSelect-select": {
                py: 0.5,
                fontSize: "0.875rem",
              },
            }}
          />
        </Tooltip>
      </Box>
    </Box>
  );
};

export default FilterSection;
